{abs, ceil, floor, max, min, round} := Math
{assign} := Object

{
  Container
  Graphics
  ObservablePoint
  Sprite
  Text
  Texture
} from "pixi.js"

{ MidiEvent, clamp } from @danielx/lib

{
  PIXELS_PER_BEAT
  CHANNEL_COLORS
  FINGERING_COLORS
  JUDGMENT_COLORS
  JUDGMENT_FEEDBACK_MS
  JUDGMENT_LABELS
  ROLL_BG_COLOR
} from ./const.civet
{ fingeringDisplay } from ./lib/midi/practice-metadata.civet
{ BURST_MAX_AGE_MS, BurstLayer, TextSpritePool, comboGlow, spawnJudgedBurst } from ./note-fx.civet
{ ARCADE_TUNING, JudgmentFx } from ./arcade-fx.civet
{ rollInteraction } from ./roll-interaction.civet
{ noteName } from ./lib/util.civet

type MidiSong = import('./lib/midi/song.civet').default

// Synthesia-style falling-note view: time runs down the screen, notes land on a
// piano keyboard drawn across the bottom. The hit line is the keyboard's top
// edge — a note sounds the moment its rectangle touches it. Shares the staff
// view's scale (PIXELS_PER_BEAT vertical pixels per beat) and channel colors.

// Pitch classes of the white keys within an octave
WHITE_PCS := [0, 2, 4, 5, 7, 9, 11]
BLACK_PCS := [1, 3, 6, 8, 10]
// Index of the white key at or below each pitch class
WHITE_INDEX := [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6]

isBlack := (n: number) ->
  BLACK_PCS.includes n % 12

// Default keyboard span when the song has no notes: C3..B5
DEFAULT_LOW := 48
DEFAULT_HIGH := 83

// Worst-first, for collapsing an onset's judgments into the one it displays
JUDGMENT_SEVERITY := ["miss", "wrong", "late", "early", "good", "perfect"]

GRID_COLOR := 0xFFFFFF
// beats of approach over which a note glows before reaching the hit line
GLOW_BEATS := 1

PianoRoll := (screenHalfSize: ObservablePoint, player: Player) ->
  container := new Container

  deferCleanup := [] as (()=>)[]

  bg := new Sprite Texture.WHITE
  bg.tint = ROLL_BG_COLOR
  container.addChild bg

  // grid lines + falling notes, redrawn every frame
  notes := new Graphics
  container.addChild notes

  playheadLine := new Sprite Texture.WHITE
  assign playheadLine,
    height: 2
    alpha: 0.75
    tint: 0x673AB7
  container.addChild playheadLine

  // white keys, redrawn on layout changes only
  whiteKeys := new Graphics
  container.addChild whiteKeys

  // key highlights and black keys, redrawn every frame (black keys are drawn
  // here rather than with the whites so a lit white key can't paint over them)
  dynamic := new Graphics
  container.addChild dynamic

  labels := new Container
  container.addChild labels

  // fingering labels riding on the falling notes (pooled, see note-fx)
  fingerPool := TextSpritePool container, fontSize: 12

  drawFingerLabel := (text: string, hand: string, x: number, w: number, y: number) ->
    return unless s := fingerPool.get text
    s.tint = FINGERING_COLORS[hand] ?? 0xFFFFFF
    s.x = floor x + (w - s.width) / 2
    s.y = floor y
    return

  // the big arcade judgment popup slamming into the center of the field
  // (arcade-fx) — one at a time, owned by the newest onset
  judgmentFx := JudgmentFx container, fontSize: 72

  // combo-tier hit bursts off the hit line (see note-fx.civet)
  bursts := BurstLayer container
  burstsSeenThrough .= 0

  // The onset owning the center popup, and where in the animation it began:
  // a refresh that lands on a popup still showing starts at the landed pose
  // (popMs) rather than flying in again — see render
  popupAt .= -Infinity
  popupAgeOffset .= 0

  // Keyboard span, always octave-aligned so the pattern of blacks is complete
  viewLow .= DEFAULT_LOW
  viewHigh .= DEFAULT_HIGH

  // Layout, computed in layoutKeyboard
  whiteCount .= 21
  ww .= 24 // white key width
  bw .= 14 // black key width
  keyboardHeight .= 96
  blackHeight .= 58
  hitY .= 384
  currentBeat .= 0
  // per-frame: the song's fingering annotations (if any) and tick scale
  let fingering: Record<string, string>?
  tpb .= 96

  whiteIndexOf := (n: number) ->
    floor((n - viewLow) / 12) * 7 + WHITE_INDEX[n % 12]

  // Midi note of the i-th white key from the left
  whiteNoteAt := (i: number) ->
    viewLow + floor(i / 7) * 12 + WHITE_PCS[i % 7]

  // x extent of a note's key (and of its falling rectangle)
  noteGeometry := (n: number) ->
    wi := whiteIndexOf clamp n, viewLow, viewHigh

    if isBlack n
      x: (wi + 1) * ww - bw / 2
      w: bw
    else
      x: wi * ww
      w: ww

  // Inverse of noteGeometry: blacks claim their narrow span at the boundary
  // between whites; below their bottom edge only whites are hit.
  noteAtX := (x: number, y: number) ->
    wi := clamp floor(x / ww), 0, whiteCount - 1
    n := whiteNoteAt wi

    unless y >= hitY + blackHeight
      if x > (wi + 1) * ww - bw / 2 and isBlack(n + 1) and n + 1 <= viewHigh
        return n + 1
      if x < wi * ww + bw / 2 and isBlack(n - 1) and n - 1 >= viewLow
        return n - 1

    n

  layoutKeyboard := ->
    {x: shw, y: shh} := screenHalfSize
    sw := 2 * shw
    sh := 2 * shh

    whiteCount = whiteIndexOf(viewHigh) + 1
    ww = sw / whiteCount
    bw = max 4, round ww * 0.6
    keyboardHeight = clamp round(sh / 6), 56, 160
    blackHeight = round keyboardHeight * 0.62
    hitY = sh - keyboardHeight

    bg.width = sw
    bg.height = sh

    playheadLine.width = sw
    playheadLine.y = hitY - 1

    whiteKeys.clear()
    labels.removeChildren().forEach (t) -> t.destroy()

    for i of [0...whiteCount]
      n := whiteNoteAt i
      whiteKeys.lineStyle 1, 0x303040, 1
      whiteKeys.beginFill 0xFAFAFA
      whiteKeys.drawRect i * ww, hitY, ww, keyboardHeight
      whiteKeys.endFill()

      if n % 12 is 0 // label the Cs
        label := new Text noteName(n),
          fontFamily: "sans-serif"
          fontSize: 12
          fill: 0x808080
        label.x = floor i * ww + (ww - label.width) / 2
        label.y = sh - label.height - 4
        labels.addChild label

  layoutKeyboard()

  // Per-frame scratch state
  noteStartMap := new Map<number, {c: number, n: number, beats: number[]}>
  sounding := [] as [number, number][] // [note, channel]

  drawBlackKeys := (gfx: Graphics) ->
    for i of [0...whiteCount]
      n := whiteNoteAt(i) + 1
      continue unless isBlack(n) and n <= viewHigh
      gfx.beginFill 0x202028
      gfx.drawRect (i + 1) * ww - bw / 2, hitY, bw, blackHeight
      gfx.endFill()

  drawNote := (c: number, n: number, startBeat: number, endBeat: number, screenTopBeat: number) ->
    return if endBeat <= currentBeat or startBeat >= screenTopBeat

    {x, w} := noteGeometry n
    top := hitY - (endBeat - currentBeat) * PIXELS_PER_BEAT
    bottom := hitY - max(0, startBeat - currentBeat) * PIXELS_PER_BEAT
    h := bottom - max(top, -PIXELS_PER_BEAT)

    active := startBeat <= currentBeat < endBeat
    color := CHANNEL_COLORS[c]

    // approach glow: a halo swells under the note as it nears the hit line
    approach := (startBeat - currentBeat) / GLOW_BEATS
    if 0 < approach < 1
      notes.lineStyle 0
      notes.beginFill color, 0.3 * (1 - approach)
      notes.drawRoundedRect x - 2, bottom - h - 3, w + 4, h + 6, min(6, h / 2 + 3)
      notes.endFill()

    if active
      notes.lineStyle 2, 0xFFFFFF, 0.9
      sounding.push [n, c]
    else
      // subtle border separates stacked notes on the dark field
      notes.lineStyle 1, 0xFFFFFF, 0.18

    notes.beginFill color, if active then 1 else 0.85
    notes.drawRoundedRect x + 1, bottom - h, w - 2, h, min(3, h / 2)
    notes.endFill()

    // Fingering rides at the leading (bottom) edge of the falling note
    if fingering and h > 16
      if hint := fingering[`${round startBeat * tpb}:${c}:${n}`]
        {text, hand} := fingeringDisplay hint
        drawFingerLabel text, hand, x, w, bottom - 16

  render := (song: MidiSong, _currentBeat: number) ->
    currentBeat = _currentBeat
    {ticksPerBeat, channelActive} := song
    screenTopBeat := currentBeat + hitY / PIXELS_PER_BEAT

    notes.clear()
    dynamic.clear()
    sounding.length = 0
    noteStartMap.clear()
    fingering = player.practiceFingering?()
    tpb = ticksPerBeat
    // song-theme overrides (cached in the accessors)
    jc := player.practiceJudgmentColors?() ?? JUDGMENT_COLORS
    bg.tint = player.rollBackground?() ?? ROLL_BG_COLOR
    // hoisted out of the per-event walk below: practiceHidesChannel reads
    // an Observable, and per event per frame that dominates large songs
    hidden := [0...16].map (c) => player.practiceHidesChannel?.(c) ?? false

    // Grid: a faint line per beat, a stronger one per measure. A song with a
    // pickup can shift the whole grid later (gridBeatOffset, in beats) so the
    // lines land on the audible downbeats rather than the file's tick zero.
    gridOffset := player.gridBeatOffset?() ?? 0
    notes.lineStyle 1, GRID_COLOR, 0.08
    for b of [max(0, ceil(currentBeat - gridOffset))...max(0, ceil(screenTopBeat - gridOffset))]
      y := hitY - (b + gridOffset - currentBeat) * PIXELS_PER_BEAT
      notes.moveTo 0, y
      notes.lineTo bg.width, y
    notes.lineStyle 1, GRID_COLOR, 0.25
    song.measureNumbers.forEach ({beat}) ->
      shifted := beat + gridOffset
      return unless currentBeat <= shifted < screenTopBeat
      y := hitY - (shifted - currentBeat) * PIXELS_PER_BEAT
      notes.moveTo 0, y
      notes.lineTo bg.width, y

    minSeen .= Infinity
    maxSeen .= -Infinity

    song.events.forEach (e: MidiEvent) ->
      {type} := e
      return unless type is "noteOn" or type is "noteOff"
      {c, n} := e
      return unless channelActive[c]
      return if hidden[c]

      beat := e.tick / ticksPerBeat
      k := (c << 7) + n

      if type is "noteOn"
        minSeen = min minSeen, n
        maxSeen = max maxSeen, n
        entry .= noteStartMap.get k
        unless entry
          entry = {c, n, beats: []}
          noteStartMap.set k, entry
        entry.beats.push beat
      else
        entry := noteStartMap.get k
        return unless entry and entry.beats.length
        drawNote c, n, entry.beats.shift()!, beat, screenTopBeat

    // Notes with no matching off run to the song's end
    songBeatLength := song.beatLength()
    noteStartMap.forEach ({c, n, beats}) ->
      beats.forEach (startBeat) ->
        drawNote c, n, startBeat, songBeatLength, screenTopBeat

    // Grow (never shrink mid-song) the keyboard to cover everything played;
    // the next frame draws with the new layout
    if maxSeen >= 0
      lo := floor(minSeen / 12) * 12
      hi := ceil((maxSeen + 1) / 12) * 12 - 1
      if lo < viewLow or hi > viewHigh
        viewLow = min viewLow, lo
        viewHigh = max viewHigh, hi
        layoutKeyboard()

    // Practice judgment feedback: flash the played key in the judgment color
    // and float the label up from the hit line as it fades. An offline render
    // supplies its own clock (fxNow) — its frames don't advance in wall time.
    now := player.fxNow?() ?? performance.now()
    flashes: [number, number, number][] := [] // [note, color, fade]
    player.practiceFeedback?.forEach ({n, judgment, at}) ->
      age := now - at
      return if age > JUDGMENT_FEEDBACK_MS
      flashes.push [n, jc[judgment] ?? 0xFFFFFF, 1 - age / JUDGMENT_FEEDBACK_MS]

    // Light the keys under sounding notes: whites first, then the black keys,
    // then lit blacks, so highlights never bleed across the key above
    sounding.forEach ([n, c]) ->
      return if isBlack n
      {x, w} := noteGeometry n
      dynamic.beginFill CHANNEL_COLORS[c], 0.8
      dynamic.drawRect x + 1, hitY + 1, w - 2, keyboardHeight - 2
      dynamic.endFill()

    flashes.forEach ([n, color, fade]) ->
      return if isBlack n
      {x, w} := noteGeometry n
      dynamic.beginFill color, 0.7 * fade
      dynamic.drawRect x + 1, hitY + 1, w - 2, keyboardHeight - 2
      dynamic.endFill()

    drawBlackKeys dynamic

    sounding.forEach ([n, c]) ->
      return unless isBlack n
      {x, w} := noteGeometry n
      dynamic.beginFill CHANNEL_COLORS[c], 0.8
      dynamic.drawRect x + 1, hitY + 1, w - 2, blackHeight - 2
      dynamic.endFill()

    flashes.forEach ([n, color, fade]) ->
      return unless isBlack n
      {x, w} := noteGeometry n
      dynamic.beginFill color, 0.7 * fade
      dynamic.drawRect x + 1, hitY + 1, w - 2, blackHeight - 2
      dynamic.endFill()

    // High-combo juice: the hit line ignites and pulses as the multiplier
    // climbs (game mode only — practice keeps a calm field)
    glow := comboGlow player, now
    if glow > 0
      dynamic.lineStyle 0
      dynamic.beginFill 0xFFFFFF, glow
      dynamic.drawRect 0, hitY - 16, bg.width, 16
      dynamic.endFill()
      dynamic.beginFill 0xFFFFFF, glow * 2
      dynamic.drawRect 0, hitY - 5, bg.width, 5
      dynamic.endFill()

    // game mode goes full arcade; practice keeps the same look at a calmer size
    fxScale := if player.playMode?.() is "game" then 1 else 0.6
    let newest: {judgment: string, beat: number, at: number, tier: number}?
    player.practiceFeedback?.forEach (f) ->
      {n, judgment, at, tier} := f
      age := now - at
      color := jc[judgment] ?? 0xFFFFFF
      // a fresh hit not yet seen throws its sparks off the hit line, sized
      // by tier (fresh: the cursor alone would replay stale entries when
      // the view starts rendering again)
      if at > burstsSeenThrough and age < BURST_MAX_AGE_MS
        {x, w} := noteGeometry n
        spawnJudgedBurst bursts, x + w / 2, hitY, color, judgment, tier
      newest = f if age <= JUDGMENT_FEEDBACK_MS and (not newest or at >= newest.at)
    // ONE popup, big, in the center of the field — not one per note. The
    // newest onset owns it (chart beats within a 64th are one onset — a
    // chord), and a fresh onset restarts the slam in place rather than
    // stacking. It shows the onset's worst judgment: a chord with a graze
    // in it reads as the graze, not as its best note.
    if newest
      onset := player.practiceFeedback!.filter (f) -> abs(f.beat - newest!.beat) < 1/16
      judgment := JUDGMENT_SEVERITY.find((j) -> onset.some (f) -> f.judgment is j) ?? newest.judgment
      tier := onset.reduce ((t, f) -> max t, f.tier), 1
      color := jc[judgment] ?? 0xFFFFFF
      if newest.at > popupAt
        // Refreshing a popup that is still up snaps it to the landed pose —
        // post swoop-in, pre-bounce — so a run of onsets punches in place
        // instead of flying in over and over. Only a popup arriving to an
        // empty field gets the full slam.
        stillUp := now - popupAt + popupAgeOffset <= JUDGMENT_FEEDBACK_MS
        popupAgeOffset = if stillUp then ARCADE_TUNING.popMs else 0
        popupAt = newest.at
      age := now - newest.at + popupAgeOffset
      judgmentFx.pop JUDGMENT_LABELS[judgment] ?? judgment, color, bg.width / 2, hitY / 2, age, {tier, judgment, scale: fxScale}
    judgmentFx.finish now
    burstsSeenThrough = now
    bursts.step now

    fingerPool.finish()

  screenPositionToBeatNote := (x: number, y: number, _event: MouseEvent) ->
    beat: currentBeat + max(0, hitY - y) / PIXELS_PER_BEAT
    note: noteAtX x, y

  // Tool + keyboard-preview wiring shared with the horizontal roll
  rollInteraction {
    player
    container
    deferCleanup
    screenPositionToBeatNote
    keyboardNoteAt: (x: number, y: number) -> noteAtX(x, y) if y >= hitY
  }

  container: container
  render: render

  resize: ->
    layoutKeyboard()

  songLoaded: (song: MidiSong) ->
    lo .= Infinity
    hi .= -Infinity
    song.events.forEach (e: MidiEvent) ->
      if e.type is "noteOn"
        {n} := e
        lo = min lo, n
        hi = max hi, n

    if hi >= 0
      viewLow = floor(lo / 12) * 12
      viewHigh = max ceil((hi + 1) / 12) * 12 - 1, viewLow + 23
    else
      viewLow = DEFAULT_LOW
      viewHigh = DEFAULT_HIGH

    layoutKeyboard()
    return

  screenPositionToBeatNote: screenPositionToBeatNote

  dispose: ->
    deferCleanup.forEach (fn) -> fn()

export default PianoRoll
